import type { PropsWithChildren } from "react";
import { createContext, useContext } from "react";

import type { TiersNames } from "../../TierPointsProgressBar";
import type { CartOverviewTierBenefit } from "../components/BenefitsList";
import { type CartOverviewTierNextTier, LoyaltyTier } from "../constants/loyaltyTiers";
import { LoyaltyBenefitIcon } from "../loyaltyBenefitIcons";

export type CartOverviewTierScenario = "upgrade" | "retention" | "downgrade" | "downgradeRetained" | "max";
export type { CartOverviewTierNextTier } from "../constants/loyaltyTiers";

export type CartOverviewTierStatusMessages = {
  statusMessageUpgrade: string;
  statusMessageRetention: string;
  statusMessageDowngrade: string;
  statusMessageDowngradeRetained: string;
  statusMessageMax: string;
};

export type CartOverviewTierBenefitsConfig = {
  title?: string;
  items?: { icon: string; text: string }[];
};

// Raw data the feature is rendered with.
export type CartOverviewTierSource = {
  cartPoints: number;
  isLoading?: boolean;
  scenario: CartOverviewTierScenario;
  nextTier?: CartOverviewTierNextTier;
  userTierName: string;
  cumulativePoints: number;
  pointsToNextTier: number;
  pointsToNextTierWithPurchase: number;
  pointsToMaintainCurrentTier: number;
  pointsToMaintainCurrentTierWithPurchase: number;
  pointsForGoldTier?: number;
  pointsForBlackTier?: number;
  tiersNames?: TiersNames;
  title?: string;
  descriptionCollapsed?: string;
  descriptionExpanded?: string;
  defaultCollapsed?: boolean;
  pointsLabel: string;
  collectMessage: string;
  statusMessages: CartOverviewTierStatusMessages;
  knowMoreUrl?: string;
  knowMoreLabel?: string;
  showBenefits?: boolean;
  goldBenefits?: CartOverviewTierBenefitsConfig;
  blackBenefits?: CartOverviewTierBenefitsConfig;
};

// Prepared value shared across the feature.
export type CartOverviewTierData = {
  points?: number;
  pointsLabel: string;
  pointsText: string;
  knowMoreUrl?: string;
  knowMoreLabel?: string;
  statusMessage: string;
  currentPoints: number;
  tier: string;
  pointsForGoldTier?: number;
  pointsForBlackTier?: number;
  tiersNames?: TiersNames;
  benefitsTitle?: string;
  benefits?: CartOverviewTierBenefit[];
  showBenefits?: boolean;
  hasTierContent?: boolean;
  title?: string;
  descriptionCollapsed?: string;
  descriptionExpanded?: string;
  defaultCollapsed?: boolean;
  isLoading?: boolean;
};

const POINTS_FALLBACK_TOKEN = "{points}";
const PURCHASE_POINTS_TOKEN = "{purchasePoints}";
const POINTS_TO_NEXT_TIER_TOKEN = "{pointsToNextTier}";
const POINTS_TO_NEXT_TIER_WITH_PURCHASE_TOKEN = "{pointsToNextTierWithPurchase}";
const POINTS_TO_MAINTAIN_CURRENT_TIER_TOKEN = "{pointsToMaintainCurrentTier}";
const POINTS_TO_MAINTAIN_CURRENT_TIER_WITH_PURCHASE_TOKEN = "{pointsToMaintainCurrentTierWithPurchase}";
const CURRENT_TIER_TOKEN = "{currentTier}";
const NEXT_TIER_TOKEN = "{nextTier}";

const STATUS_MESSAGE_BY_SCENARIO: Record<CartOverviewTierScenario, keyof CartOverviewTierStatusMessages> = {
  upgrade: "statusMessageUpgrade",
  retention: "statusMessageRetention",
  downgrade: "statusMessageDowngrade",
  downgradeRetained: "statusMessageDowngradeRetained",
  max: "statusMessageMax",
};

const capitalizeFirstLetter = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1);

export const prepareCartOverviewTierValue = ({
  cartPoints,
  isLoading,
  scenario,
  nextTier,
  userTierName,
  cumulativePoints,
  pointsToMaintainCurrentTier,
  pointsToMaintainCurrentTierWithPurchase,
  pointsToNextTier,
  pointsToNextTierWithPurchase,
  pointsForGoldTier,
  pointsForBlackTier,
  tiersNames,
  title,
  descriptionCollapsed,
  descriptionExpanded,
  defaultCollapsed,
  pointsLabel,
  collectMessage,
  statusMessages,
  knowMoreUrl,
  knowMoreLabel,
  showBenefits,
  goldBenefits,
  blackBenefits,
}: CartOverviewTierSource): CartOverviewTierData => {
  const statusMessage = statusMessages[STATUS_MESSAGE_BY_SCENARIO[scenario]]
    .replaceAll(POINTS_FALLBACK_TOKEN, String(cartPoints))
    .replaceAll(PURCHASE_POINTS_TOKEN, String(cartPoints))
    .replaceAll(POINTS_TO_NEXT_TIER_WITH_PURCHASE_TOKEN, String(pointsToNextTierWithPurchase))
    .replaceAll(POINTS_TO_NEXT_TIER_TOKEN, String(pointsToNextTier))
    .replaceAll(POINTS_TO_MAINTAIN_CURRENT_TIER_TOKEN, String(pointsToMaintainCurrentTier))
    .replaceAll(POINTS_TO_MAINTAIN_CURRENT_TIER_WITH_PURCHASE_TOKEN, String(pointsToMaintainCurrentTierWithPurchase))
    .replaceAll(CURRENT_TIER_TOKEN, capitalizeFirstLetter(String(userTierName)))
    .replaceAll(NEXT_TIER_TOKEN, capitalizeFirstLetter(String(nextTier)));

  const pointsText = collectMessage
    .replaceAll(POINTS_FALLBACK_TOKEN, String(cartPoints))
    .replaceAll(PURCHASE_POINTS_TOKEN, String(cartPoints));

  const isProgressScenario = scenario === "upgrade" || scenario === "retention";
  const activeBenefits =
    nextTier === LoyaltyTier.Black ? blackBenefits : nextTier === LoyaltyTier.Gold ? goldBenefits : undefined;

  const benefits =
    isProgressScenario && showBenefits
      ? (activeBenefits?.items ?? []).map(({ icon, text }) => ({
          icon: <LoyaltyBenefitIcon name={icon} />,
          text,
        }))
      : [];

  const hasProgressBar = pointsForGoldTier !== undefined || pointsForBlackTier !== undefined;
  const hasTierContent = hasProgressBar || Boolean(benefits?.length);

  return {
    isLoading,
    title,
    descriptionCollapsed,
    descriptionExpanded,
    defaultCollapsed,
    points: cartPoints,
    pointsLabel,
    pointsText,
    knowMoreUrl,
    knowMoreLabel,
    statusMessage,
    currentPoints: cumulativePoints,
    tier: userTierName,
    pointsForGoldTier,
    pointsForBlackTier,
    tiersNames,
    benefitsTitle: activeBenefits?.title,
    benefits,
    showBenefits: benefits.length > 0,
    hasTierContent,
  };
};

const CartOverviewTierContext = createContext<CartOverviewTierData | null>(null);

export const CartOverviewTierProvider = ({ children, ...source }: PropsWithChildren<CartOverviewTierSource>) => (
  <CartOverviewTierContext.Provider value={prepareCartOverviewTierValue(source)}>
    {children}
  </CartOverviewTierContext.Provider>
);

export const useCartOverviewTierContext = () => {
  const context = useContext(CartOverviewTierContext);

  if (!context) {
    throw new Error("useCartOverviewTierContext must be used within CartOverviewTierProvider");
  }

  return context;
};
